import theme, { type GlobalToken } from "antd/lib/theme";
import clsx from "clsx";

export enum SkillLevelIndicatorStatus {
    FULL = "full",
    PARTIAL = "partial",
    EMPTY = "empty",
}

type SkillLevelIndicatorProps = {
    levels: [SkillLevelIndicatorStatus, SkillLevelIndicatorStatus, SkillLevelIndicatorStatus];
} & React.ComponentProps<"svg">;

function mapSkillLevelIndicatorStatusToColor(token: GlobalToken, status: SkillLevelIndicatorStatus): string {
    const colors = {
        [SkillLevelIndicatorStatus.FULL]: token.colorPrimaryActive,
        [SkillLevelIndicatorStatus.PARTIAL]: token.colorPrimaryBorderHover,
        [SkillLevelIndicatorStatus.EMPTY]: token.colorFill,
    } as const;

    return colors[status];
}

export const SkillLevelIndicator = ({ levels, className, ...props }: SkillLevelIndicatorProps) => {
    const { token } = theme.useToken();
    const size = 30; // width totale = largeur des barres + espaces entre elles
    const barWidth = 8;
    const spacing = 3; // Espacement entre les barres
    const padding = 0; // Padding autour des barres

    const getBarHeight = (level: number) => {
        const LEVEL_BASE_HEIGHT = 8;
        return level * LEVEL_BASE_HEIGHT;
    };

    const offsetY = (size - getBarHeight(levels.length)) / 2; // Centrer verticalement en fonction de la barre la plus haute

    // Position Y : toutes alignées en bas
    const bottomY = size - padding - offsetY;

    const bars = levels.map((status, index) => {
        const level = index + 1;

        const levelHeight = getBarHeight(level);

        return {
            x: padding + index * (barWidth + spacing),
            y: bottomY - levelHeight,
            width: barWidth,
            height: levelHeight,
            color: mapSkillLevelIndicatorStatusToColor(token, status),
            level,
            status,
        };
    });

    return (
        <svg {...props} className={clsx(className, "size-[1.5em]")} viewBox={`0 0 ${size} ${size}`}>
            {bars.map((bar, index) => (
                <rect
                    key={index}
                    x={bar.x}
                    y={bar.y}
                    width={bar.width}
                    height={bar.height}
                    fill={bar.color}
                    rx={2}
                    data-status={bar.status}
                />
            ))}
        </svg>
    );
};
